import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";

import { useFormik } from "formik";
import * as Yup from "yup";
import Table from "../../components/Table";
import { format } from "date-fns";
import AddDialog from "../../components/Modal/AddModal";
import TextInput from "../../components/Select/InputText";
import SelectInput from "../../components/Select/SelectInput";
import { useAuth } from "../../context/AuthContext";
import { addMemberService, deleteMemberService, getMembers, updateMemberService } from "../../services/membersService";
import { getMemberTypeService } from "../../services/memberTypeService";
import { getBranchService } from "../../services/branchService";
import { getPackages } from "../../services/packagesService";
import { renewService } from "../../services/renewService";
import { API_ORIGIN } from "../../services/httpService";
import { showErrorToast, showSuccessToast } from "../../services/toastService";
import { FaCamera, FaPencilAlt, FaRedo, FaTrash, FaUpload } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import { hasPermission } from "../../utils/permissions";

const formatDate = (value?: string | null) => {
    if (!value) return "-";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";

    return format(date, "dd/MM/yyyy");
};

const emptyDobParts = { year: "", month: "", day: "" };

const parseDateParts = (value?: string) => {
    if (!value || !/^\d{4}-\d{2}-\d{2}/.test(value)) return emptyDobParts;
    const [year, month, day] = value.slice(0, 10).split("-");
    return { year, month, day };
};

const isActiveStatus = (value: unknown) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") return value.toLowerCase() === "active";
    return Boolean(value);
};

const StatusCell = ({ value }: { value: unknown }) => {
    const isActive = isActiveStatus(value);
    return (
        <span className={`px-2 py-1 rounded-md ${isActive ? "text-green-500" : "text-red-500"}`}>
            {isActive ? "Active" : "Inactive"}
        </span>
    );
};

const Members = () => {

    const { user } = useAuth();
    const { pathname } = useLocation();
    const canEditMembers = hasPermission(user, "members.write");
    const canViewSubscriptions = hasPermission(user, "subscriptions.view");
    const [addMember, setAddMember] = useState(false);
    const [renew, setRenew] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [memberTypes, setMemberTypes] = useState([]);
    const [branches, setBranchesType] = useState([]);
    const [packages, setPackages] = useState([]);
    const [selectedPackage, setSelectedPackage] = useState("");
    const [imagePreview, setImagePreview] = useState(null);
    const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
    const [startDate, setStartDate] = useState("");
    const [dobParts, setDobParts] = useState(emptyDobParts);
    const [cameraOpen, setCameraOpen] = useState(false);
    const [cameraError, setCameraError] = useState<string | null>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);

    const [selectedRowId, setSelectedRowId] = useState<string | null>(null); // Manage selected row ID
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [total, setTotal] = useState(0);
    const [searchInput, setSearchInput] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");
    const [appliedFilters, setAppliedFilters] = useState({
        gender: "",
        branch: "",
        memberType: "",
        status: "",
    });
    const selectedPackageDetails = packages.find(
        (pkg: any) => pkg._id === selectedPackage
    );
    const [filters, setFilters] = useState({
        gender: "",
        branch: "",
        memberType: "",
        status: "", // "active" | "inactive" | ""
    });

    const fetchMembers = useCallback(async (opts?: {
        page?: number;
        search?: string;
        filters?: { gender?: string; branch?: string; memberType?: string; status?: string };
    }) => {
        if (!user?.id) return;
        try {
            const response = await getMembers({
                page: opts?.page ?? page,
                limit,
                search: opts?.search ?? appliedSearch,
                gender: opts?.filters?.gender ?? appliedFilters.gender,
                branch: opts?.filters?.branch ?? appliedFilters.branch,
                memberType: opts?.filters?.memberType ?? appliedFilters.memberType,
                status: opts?.filters?.status ?? appliedFilters.status,
            });

            const members = Array.isArray(response) ? response : response?.data ?? [];
            const normalized = members.map((m: any) => ({
                ...m,
                _id: m._id ?? m.id,
            }));

            setTableData(normalized);
            if (!Array.isArray(response)) {
                setTotal(response?.total ?? normalized.length);
            } else {
                setTotal(normalized.length);
            }
        } catch (err) {
            console.error("Failed to fetch members:", err);
        }
    }, [user, page, limit, appliedSearch, appliedFilters]);

    useEffect(() => {
        fetchMembers({ page, search: appliedSearch, filters: appliedFilters });
    }, [fetchMembers, pathname, page, appliedSearch, appliedFilters]);

    useEffect(() => {
        const handleVisibility = () => {
            if (document.visibilityState === "visible") {
                fetchMembers({ page, search: appliedSearch, filters: appliedFilters });
            }
        };
        window.addEventListener("visibilitychange", handleVisibility);
        return () => window.removeEventListener("visibilitychange", handleVisibility);
    }, [fetchMembers, page, appliedSearch, appliedFilters]);
    useEffect(() => {
        const fetchPackages = async () => {
            if (!user?.id) return;
            try {
                const packages = await getPackages();
                console.log(packages)
                setPackages(packages);

            } catch (err) {
                console.error("Failed to fetch packages", err)


            }
        }
        fetchPackages();
    }, [renew])
    useEffect(() => {
        const fetchMemberTypes = async () => {
            if (!user?.id) return;
            try {
                const response = await getMemberTypeService();
                setMemberTypes(Array.isArray(response) ? response : response?.data ?? response?.items ?? []);
            } catch (err) {
                console.error("Failed to fetch member types", err);
            }
        };
        fetchMemberTypes();
    }, [user?.id]);
    useEffect(() => {
        const fetchBranches = async () => {
            if (!user?.id) return;
            try {
                const branches = await getBranchService();
                setBranchesType(branches)

            }
            catch (err) {
                console.error("Failed to fetch branches", err)
            }
        }
        fetchBranches();
    }, [user?.id])
    // Formik for form handling



    const validationSchema = useMemo(
        () =>
            Yup.object({
                firstName: Yup.string().required("First name is required"),
                lastName: Yup.string().required("Last name is required"),
                DOB: Yup.string().nullable(),
                gender: Yup.string().nullable(),
                mobile: Yup.string().nullable(),
                memberType: Yup.string().nullable(),
                branch: Yup.string().nullable(),
                address: Yup.string().nullable(),
                emergencyContact: Yup.string().nullable(),
                bloodType: Yup.string().nullable(),
                profileImage: Yup.mixed().nullable(),
                email: Yup.string()
                    .email("Invalid email address")
                    .nullable()
                    .transform((value, originalValue) =>
                        originalValue === "" ? null : value
                    ),
            }),
        []
    );

    const formik = useFormik({
        initialValues: {
            firstName: "",
            lastName: "",
            DOB: "",
            gender: "",
            mobile: "",
            memberType: "",
            branch: "",
            address: "",
            email: "",
            profileImage: null,
            emergencyContact: "",
            bloodType: "",
            currentImageUrl: "",
        },
        validationSchema,
        onSubmit: async (values) => {
            console.log("SUBMIT, editingMemberId =", editingMemberId);
            try {
                console.log("the user", user);
                if (!user) {
                    showErrorToast("User not logged in");
                    return;
                }

                console.log(
                    "Submitting member form. editingMemberId =",
                    editingMemberId
                );

                const formData = new FormData();
                const allowedFields = new Set([
                    "firstName",
                    "lastName",
                    "DOB",
                    "gender",
                    "mobile",
                    "memberType",
                    "branch",
                    "address",
                    "email",
                    "emergencyContact",
                    "bloodType",
                ]);

                Object.keys(values).forEach((key) => {
                    const value = (values as any)[key];

                    if (key === "profileImage") {
                        // Only send the file if user chose a new one
                        if (value) {
                            formData.append("profileImage", value);
                        }
                        return;
                    }

                    if (key === "currentImageUrl") {
                        // Preserve existing image on update when no new file is chosen
                        if (value) {
                            formData.append("currentImageUrl", value as any);
                        }
                        return;
                    }

                    if (allowedFields.has(key) && value !== undefined && value !== null) {
                        formData.append(key, value as any);
                    }
                });

                if (editingMemberId) {
                    // UPDATE
                    await updateMemberService(editingMemberId, formData);

                    // safest way: refetch members so table is always up to date
                    await fetchMembers({ page, search: appliedSearch, filters: appliedFilters });
                } else {
                    // CREATE
                    await addMemberService(formData);
                    await fetchMembers({ page: 1, search: appliedSearch, filters: appliedFilters });
                    setPage(1);
                }

                closePopUp();
            } catch (err) {
                console.error("Error adding/updating member:", err);
                showErrorToast("Failed to save member");
            }
        },
    });

    const handleRenewMembership = async (packageId, date) => {

        if (!selectedRowId) {
            showErrorToast("Please select a member to renew.");
            return;
        }
        const selectedMember = tableData.find((member: any) => (member?._id ?? member?.id) === selectedRowId);
        const memberTypeId = selectedMember?.memberType?._id ?? selectedMember?.memberType ?? "";
        const packageTypeId = selectedPackageDetails?.memberType?._id ?? selectedPackageDetails?.memberType ?? "";
        if (memberTypeId && packageTypeId && memberTypeId !== packageTypeId) {
            showErrorToast("Selected package is for a different member type.");
            return;
        }
        if (!date) {
            showErrorToast("Please select a start date.");
            return;
        }
        const payload = {
            memberId: selectedRowId,
            packageId: packageId,
            startDate: date,
        }
        console.log("payload", payload)

        try {
            const result = await renewService(payload);
            console.log("Renewal successful:", result);
            setRenew(false); // Close the renew dialog
            showSuccessToast("Membership renewed successfully");
        } catch (err) {
            console.error("Renewal failed:", err);
            showErrorToast("Failed to renew membership");
        }
    };
    const openPopUp = () => {
        setEditingMemberId(null);
        setImagePreview(null);
        setDobParts(emptyDobParts);
        formik.resetForm();
        setAddMember(true);
    };

    const openRenewPopUp = (memberId?: string) => {
        const renewMemberId = memberId ?? selectedRowId;
        console.log(renewMemberId);
        if (!renewMemberId) {
            showErrorToast('No Selected Member')
            return;
        }
        setSelectedRowId(renewMemberId);
        setSelectedPackage("");
        setStartDate(new Date().toISOString().split("T")[0]);
        setRenew(true)
    }
    const closeRenewPopUp = () => {
        setRenew(false);
        setStartDate("");
    }

    const handleSearch = () => {
        setAppliedFilters(filters);
        setAppliedSearch(searchInput);
        setPage(1);
        fetchMembers({ page: 1, search: searchInput, filters });
    };

    const handleClearFilters = () => {
        const cleared = { gender: "", branch: "", memberType: "", status: "" };
        setFilters(cleared);
        setSearchInput("");
        setAppliedFilters(cleared);
        setAppliedSearch("");
        setPage(1);
        fetchMembers({ page: 1, search: "", filters: cleared });
    };
    const buildImageUrl = (url?: string) => {
        if (!url) return "";
        // If it's already absolute (starts with http), keep as is
        if (url.startsWith("http://") || url.startsWith("https://")) return url;
        // Otherwise prepend your backend URL
        // Avoid double slashes
        return url.startsWith("/") ? `${API_ORIGIN}${url}` : `${API_ORIGIN}/${url}`;
    };


    const handleEditMember = (member: any) => {
        console.log("EDIT CLICKED, member:", member);
        console.log("member._id:", member._id, "member.id:", member.id);
        if (!member) return;

        const rawImage =
            member?.profileImage?.url ??
            member?.profileImage ??
            "";

        const profileImageUrl = buildImageUrl(rawImage);

        // IMPORTANT: support both _id and id
        setEditingMemberId(member._id ?? member.id ?? null);

        setImagePreview(profileImageUrl || null);

        const dobValue = member.DOB ? member.DOB.split("T")[0] : "";
        setDobParts(parseDateParts(dobValue));

        formik.setValues({
            firstName: member.firstName ?? "",
            lastName: member.lastName ?? "",
            DOB: dobValue,
            gender: member.gender ?? "",
            mobile: member.mobile ?? "",
            memberType: member.memberType?._id ?? member.memberType ?? "",
            branch: member.branch?._id ?? member.branch ?? "",
            address: member.address ?? "",
            email: member.email ?? "",
            profileImage: null,
            emergencyContact: member.emergencyContact ?? "",
            bloodType: member.bloodType ?? "",
            currentImageUrl: profileImageUrl,
        });

        formik.setTouched({});
        console.log("Set adding member")
        setAddMember(true);
    };


    const handleDeleteMember = async (memberId?: string) => {
        if (!memberId) return;
        const confirmDelete = window.confirm("Are you sure you want to delete this member?");
        if (!confirmDelete) return;
        try {
            await deleteMemberService(memberId);
            setTableData((prevData) => prevData.filter((member: any) => member._id !== memberId));
            if (selectedRowId === memberId) {
                setSelectedRowId(null);
            }
        } catch (err) {
            console.error("Failed to delete member:", err);
            showErrorToast("Failed to delete member");
        }
    };

    const columns = [
        { Header: "First Name", accessor: "firstName" },
        { Header: "Last Name", accessor: "lastName" },
        { Header: "DOB", accessor: "DOB", Cell: ({ value }: { value: string }) => formatDate(value) },
        { Header: "Status", accessor: "status", Cell: StatusCell },
        { Header: "Mobile", accessor: "mobile" },
        { Header: "Gender", accessor: "gender" },
        { Header: "Branch", accessor: "branch.name" },
        { Header: "Member Type", accessor: "memberType.name" },
        ...(canEditMembers || canViewSubscriptions
            ? [{
                Header: 'Actions',
                accessor: 'actions',
                Cell: ({ row }) => (
                    <div className="flex flex-row gap-2">
                        {canViewSubscriptions && (
                            <button
                                type="button"
                                onClick={() => openRenewPopUp(row.original?._id ?? row.original?.id)}
                                className="text-[#ac120c] hover:text-red-800"
                                title="Renew membership"
                            >
                                <FaRedo />
                            </button>
                        )}
                        {canEditMembers && (
                            <>
                                <button
                                    type="button"
                                    onClick={() => handleEditMember(row.original)}
                                    className="text-blue-500 hover:text-blue-700"
                                >
                                    <FaPencilAlt />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleDeleteMember(row.original?._id ?? row.original?.id)}
                                    className="text-red-500 hover:text-red-700"
                                >
                                    <FaTrash />
                                </button>
                            </>
                        )}
                    </div>
                ),
            }]
            : [])

    ];

    const genderCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        tableData.forEach((member: any) => {
            const key = member?.gender ?? "Unknown";
            counts[key] = (counts[key] ?? 0) + 1;
        });
        return counts;
    }, [tableData]);

    const branchCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        tableData.forEach((member: any) => {
            const key = member?.branch?.name ?? member?.branch ?? "Unknown";
            counts[key] = (counts[key] ?? 0) + 1;
        });
        return counts;
    }, [tableData]);

    const statusCounts = useMemo(() => {
        const counts = { active: 0, inactive: 0 };
        tableData.forEach((member: any) => {
            if (isActiveStatus(member?.status)) {
                counts.active += 1;
            } else {
                counts.inactive += 1;
            }
        });
        return counts;
    }, [tableData]);

    const totalPages = Math.max(1, Math.ceil(total / limit));

    const filteredData = tableData;

    const yearOptions = useMemo(() => {
        const currentYear = new Date().getFullYear();
        return Array.from({ length: currentYear - 1899 }, (_, index) => String(currentYear - index));
    }, []);

    const monthOptions = useMemo(
        () => Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, "0")),
        []
    );

    const dayOptions = useMemo(() => {
        const year = Number(dobParts.year || new Date().getFullYear());
        const month = Number(dobParts.month || "1");
        const daysInMonth = new Date(year, month, 0).getDate();
        return Array.from({ length: daysInMonth }, (_, index) => String(index + 1).padStart(2, "0"));
    }, [dobParts.year, dobParts.month]);

    const handleDobPartChange = (part: "year" | "month" | "day", value: string) => {
        setDobParts((prev) => {
            const next = { ...prev, [part]: value };
            const year = Number(next.year || new Date().getFullYear());
            const month = Number(next.month || "1");
            const maxDay = new Date(year, month, 0).getDate();

            if (next.day && Number(next.day) > maxDay) {
                next.day = String(maxDay).padStart(2, "0");
            }

            const dob = next.year && next.month && next.day ? `${next.year}-${next.month}-${next.day}` : "";
            formik.setFieldValue("DOB", dob);
            formik.setFieldTouched("DOB", true, false);
            return next;
        });
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files[0];
        if (file) {
            formik.setFieldValue('profileImage', file);
            formik.setFieldTouched('profileImage', true, true);
            formik.setFieldValue('currentImageUrl', '');

            // Create image preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Remove image handler
    const removeImage = () => {
        formik.setFieldValue('profileImage', null);
        formik.setFieldValue('currentImageUrl', '');
        formik.setFieldTouched('profileImage', true, true);
        setImagePreview(null);
        // Reset file input
        const fileInput = document.querySelector<HTMLInputElement>('input[type="file"]');
        if (fileInput) fileInput.value = '';
    };

    // Enhanced closePopUp function
    const closePopUp = () => {
        setAddMember(false);
        setImagePreview(null);
        setEditingMemberId(null);
        setDobParts(emptyDobParts);
        formik.resetForm();
    };

    const stopCameraStream = () => {
        if (mediaStream) {
            mediaStream.getTracks().forEach(track => track.stop());
            setMediaStream(null);
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    };

    const closeCamera = () => {
        stopCameraStream();
        setCameraOpen(false);
        setCameraError(null);
    };

    const handleOpenCamera = async () => {
        setCameraOpen(true);
        if (!navigator.mediaDevices?.getUserMedia) {
            setCameraError("Camera access is not supported on this device.");
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "user" }
            });
            setMediaStream(stream);
            setCameraError(null);
            const videoElement = videoRef.current;
            if (videoElement) {
                videoElement.srcObject = stream;
                videoElement.onloadedmetadata = () => {
                    videoElement.play().catch(() => null);
                };
            }
        } catch (error) {
            console.error("Failed to access camera:", error);
            setCameraError("Unable to access the camera. Please verify permissions.");
        }
    };

    const capturePhoto = () => {
        if (!videoRef.current || !canvasRef.current) return;
        const videoElement = videoRef.current;
        const canvasElement = canvasRef.current;
        canvasElement.width = videoElement.videoWidth;
        canvasElement.height = videoElement.videoHeight;
        const context = canvasElement.getContext("2d");
        if (!context) return;
        context.drawImage(videoElement, 0, 0, canvasElement.width, canvasElement.height);
        canvasElement.toBlob((blob) => {
            if (!blob) return;
            const file = new File([blob], `selfie-${Date.now()}.png`, { type: "image/png" });
            formik.setFieldValue("profileImage", file);
            formik.setFieldValue("currentImageUrl", "");
            formik.setFieldTouched("profileImage", true, true);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(blob);
            closeCamera();
        }, "image/png");
    };

    useEffect(() => {
        return () => {
            if (mediaStream) {
                mediaStream.getTracks().forEach(track => track.stop());
            }
        };
    }, [mediaStream]);


    return (
        <div className="p-6 w-full">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h1 className="text-2xl font-bold">Members</h1>
                    <p className="text-sm text-gray-500">Manage members and filter by key attributes</p>
                </div>
                <div className="text-sm text-gray-500">
                    Total: <span className="font-semibold text-gray-900">{total}</span>
                </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-semibold text-gray-900">Filters</h2>
                    <button
                        type="button"
                        onClick={handleClearFilters}
                        className="text-xs text-gray-600 hover:text-gray-900"
                    >
                        Clear all
                    </button>
                </div>

                <div className="flex flex-col md:flex-row gap-3 mb-4">
                    <input
                        type="text"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Search by name, phone, email..."
                        className="border p-2 rounded-md w-full md:w-1/3"
                    />
                    <button
                        type="button"
                        onClick={handleSearch}
                        className="bg-[#ac120c] text-white px-4 py-2 rounded-md w-full md:w-auto"
                    >
                        Search
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Gender</label>
                        <SelectInput
                            name="gender"
                            value={filters.gender}
                            onChange={(e) => setFilters((prev) => ({ ...prev, gender: e.target.value }))}
                            options={[
                                { value: "", label: "All" },
                                { value: "Male", label: "Male" },
                                { value: "Female", label: "Female" },
                            ]}
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Member Type</label>
                        <SelectInput
                            name="memberType"
                            value={filters.memberType}
                            onChange={(e) => setFilters((prev) => ({ ...prev, memberType: e.target.value }))}
                            options={[
                                { value: "", label: "All" },
                                ...memberTypes.map((type: any) => ({
                                    value: type._id ?? type.id,
                                    label: type.name,
                                })),
                            ]}
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Branch</label>
                        <SelectInput
                            name="branch"
                            value={filters.branch}
                            onChange={(e) => setFilters((prev) => ({ ...prev, branch: e.target.value }))}
                            options={[
                                { value: "", label: "All" },
                                ...branches.map((branch: any) => ({
                                    value: branch._id,
                                    label: branch.name,
                                })),
                            ]}
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Status</label>
                        <SelectInput
                            name="status"
                            value={filters.status}
                            onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
                            options={[
                                { value: "", label: "All" },
                                { value: "active", label: "Active" },
                                { value: "inactive", label: "Inactive" },
                            ]}
                        />
                    </div>
                </div>

            </div>
            <Table
                columns={columns}
                data={filteredData}
                pageSize={limit}
                add={canEditMembers}
                onOpen={openPopUp}
                checkbox={false}
                showSearch={false}
                showPagination={false}
                selectedRowId={selectedRowId}  // Pass selected row ID as prop
                setSelectedRowId={setSelectedRowId}  // Pass setSelectedRowId function to update the state
            />
            <div className="flex justify-between items-center mt-4">
                <button
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    disabled={page <= 1}
                    className="px-4 py-2 bg-gray-800 text-white rounded disabled:opacity-50"
                >
                    Previous
                </button>
                <span>Page {page} of {totalPages}</span>
                <button
                    onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={page >= totalPages}
                    className="px-4 py-2 bg-gray-800 text-white rounded disabled:opacity-50"
                >
                    Next
                </button>
            </div>

            {/* Popup Dialog for Adding a Member */}
            <AddDialog
                title={editingMemberId ? "Edit Member" : "Add New Member"}
                isOpen={addMember}
                onClose={closePopUp}
                maxWidth="max-w-5xl" // Made slightly wider for better 2x2 layout
            >
                <div className="p-6">
                    <form onSubmit={formik.handleSubmit} className="space-y-8">
                        {/* Profile Picture Section */}
                        <div className="flex flex-col items-center justify-center gap-4 mb-8 md:flex-row">
                            <div className="w-32 h-32 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden">
                                {imagePreview ? (
                                    <img
                                        src={imagePreview}
                                        alt="Profile preview"
                                        className="w-full h-full object-cover rounded-full"
                                    />
                                ) : (
                                    <div className="text-center">
                                        <svg className="w-12 h-12 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                        <p className="text-xs text-gray-500">Profile Picture</p>
                                    </div>
                                )}
                            </div>
                            {/* Upload/Remove buttons */}
                            <div className="flex flex-wrap items-center justify-center gap-2">

                                {/* From files / documents / gallery */}
                                <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-blue-200 bg-white px-4 py-2 text-xs font-medium text-blue-600 shadow-sm transition hover:bg-blue-50">
                                    <FaUpload className="text-blue-500" />
                                    <span>{imagePreview ? 'Change from Files' : 'Upload from Files'}</span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        className="hidden"
                                    />
                                </label>

                                {/* Take selfie (camera) */}
                                <button
                                    type="button"
                                    onClick={handleOpenCamera}
                                    className="inline-flex items-center gap-2 rounded-full border border-green-200 bg-white px-4 py-2 text-xs font-medium text-green-600 shadow-sm transition hover:bg-green-50"
                                >
                                    {imagePreview ? (
                                        <>
                                            <FaRedo className="text-green-500" />
                                            <span>Retake Selfie</span>
                                        </>
                                    ) : (
                                        <>
                                            <FaCamera className="text-green-500" />
                                            <span>Take Selfie</span>
                                        </>
                                    )}
                                </button>

                                {imagePreview && (
                                    <button
                                        type="button"
                                        onClick={removeImage}
                                        className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-white px-4 py-2 text-xs font-medium text-red-600 shadow-sm transition hover:bg-red-50"
                                    >
                                        <FaTrash className="text-red-500" />
                                        <span>Remove</span>
                                    </button>
                                )}
                            </div>
                        </div>
                        {(formik.touched.profileImage || formik.submitCount > 0) && formik.errors.profileImage && (
                            <p className="text-red-500 text-xs text-center -mt-4 mb-4">{String(formik.errors.profileImage)}</p>
                        )}

                        {/* 2x2 Grid Layout for Form Sections */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Row 1, Column 1: Personal Information */}
                            <div className="bg-gray-50 p-6 rounded-lg h-fit">
                                <h3 className="text-lg font-semibold text-gray-900  flex items-center">
                                    <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                    Personal Information
                                </h3>
                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <TextInput
                                                label="First Name"
                                                type="text"
                                                name="firstName"
                                                value={formik.values.firstName}
                                                onChange={formik.handleChange}
                                                placeholder="Enter first name"
                                            />
                                            {formik.touched.firstName && formik.errors.firstName && (
                                                <p className="text-red-500 text-sm mt-1">{formik.errors.firstName}</p>
                                            )}
                                        </div>
                                        <div>
                                            <TextInput
                                                label="Last Name"
                                                type="text"
                                                name="lastName"
                                                value={formik.values.lastName}
                                                onChange={formik.handleChange}
                                                placeholder="Enter last name"
                                            />
                                            {formik.touched.lastName && formik.errors.lastName && (
                                                <p className="text-red-500 text-sm mt-1">{formik.errors.lastName}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-semibold">Date of Birth</label>
                                            <div className="mt-1 grid grid-cols-3 gap-2">
                                                <select
                                                    aria-label="Birth year"
                                                    value={dobParts.year}
                                                    onChange={(e) => handleDobPartChange("year", e.target.value)}
                                                    className="min-w-0 rounded border px-2 py-2"
                                                >
                                                    <option value="">Year</option>
                                                    {yearOptions.map((year) => (
                                                        <option key={year} value={year}>
                                                            {year}
                                                        </option>
                                                    ))}
                                                </select>
                                                <select
                                                    aria-label="Birth month"
                                                    value={dobParts.month}
                                                    onChange={(e) => handleDobPartChange("month", e.target.value)}
                                                    className="min-w-0 rounded border px-2 py-2"
                                                >
                                                    <option value="">Month</option>
                                                    {monthOptions.map((month) => (
                                                        <option key={month} value={month}>
                                                            {month}
                                                        </option>
                                                    ))}
                                                </select>
                                                <select
                                                    aria-label="Birth day"
                                                    value={dobParts.day}
                                                    onChange={(e) => handleDobPartChange("day", e.target.value)}
                                                    className="min-w-0 rounded border px-2 py-2"
                                                >
                                                    <option value="">Day</option>
                                                    {dayOptions.map((day) => (
                                                        <option key={day} value={day}>
                                                            {day}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            {formik.touched.DOB && formik.errors.DOB && (
                                                <p className="text-red-500 text-sm mt-1">{formik.errors.DOB}</p>
                                            )}
                                        </div>
                                        <div>
                                            <SelectInput
                                                label="Gender"
                                                name="gender"
                                                value={formik.values.gender}
                                                onChange={formik.handleChange}
                                                options={[
                                                    { value: "Male", label: "Male" },
                                                    { value: "Female", label: "Female" },
                                                ]}
                                            />
                                            {formik.touched.gender && formik.errors.gender && (
                                                <p className="text-red-500 text-sm mt-1">{formik.errors.gender}</p>
                                            )}
                                        </div>
                                    </div>
                                    <SelectInput
                                        label="Blood Type"
                                        name="bloodType"
                                        value={formik.values.bloodType}
                                        onChange={formik.handleChange}
                                        options={[
                                            { value: "A+", label: "A+" },
                                            { value: "A-", label: "A-" },
                                            { value: "B+", label: "B+" },
                                            { value: "B-", label: "B-" },
                                            { value: "AB+", label: "AB+" },
                                            { value: "AB-", label: "AB-" },
                                            { value: "O+", label: "O+" },
                                            { value: "O-", label: "O-" },
                                        ]}
                                    />
                                    {formik.touched.bloodType && formik.errors.bloodType && (
                                        <p className="text-red-500 text-sm mt-1">{formik.errors.bloodType}</p>
                                    )}
                                    <div className="space-y-4">
                                        <SelectInput
                                            label="Member Type"
                                            name="memberType"
                                            value={formik.values.memberType}
                                            onChange={formik.handleChange}
                                            options={memberTypes.map((type: any) => ({
                                                value: type._id ?? type.id,
                                                label: type.name,
                                            }))}
                                        />
                                        {formik.touched.memberType && formik.errors.memberType && (
                                            <p className="text-red-500 text-sm mt-1">{formik.errors.memberType}</p>
                                        )}
                                        <SelectInput
                                            label="Branch"
                                            name="branch"
                                            value={formik.values.branch}
                                            onChange={formik.handleChange}
                                            options={branches.map((branch) => ({
                                                value: branch._id,
                                                label: branch.name,
                                            }))}
                                        />
                                        {formik.touched.branch && formik.errors.branch && (
                                            <p className="text-red-500 text-sm mt-1">{formik.errors.branch}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Row 1, Column 2: Contact Information */}
                            <div className="bg-gray-50 p-6 rounded-lg h-fit">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                    <svg className="w-5 h-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                    Contact Information
                                </h3>
                                <div className="space-y-4">
                                    <TextInput
                                        label="Email Address"
                                        type="email"
                                        name="email"
                                        placeholder="Enter email address"
                                        value={formik.values.email}
                                        onChange={formik.handleChange}
                                    />
                                    {formik.touched.email && formik.errors.email && (
                                        <p className="text-red-500 text-sm mt-1">{formik.errors.email}</p>
                                    )}
                                    <TextInput
                                        label="Mobile Number"
                                        type="tel"
                                        name="mobile"
                                        placeholder="Enter mobile number"
                                        value={formik.values.mobile}
                                        onChange={formik.handleChange}
                                    />
                                    {formik.touched.mobile && formik.errors.mobile && (
                                        <p className="text-red-500 text-sm mt-1">{formik.errors.mobile}</p>
                                    )}
                                    <TextInput
                                        label="Emergency Contact"
                                        type="tel"
                                        name="emergencyContact"
                                        placeholder="Enter emergency contact"
                                        value={formik.values.emergencyContact}
                                        onChange={formik.handleChange}
                                    />
                                    {formik.touched.emergencyContact && formik.errors.emergencyContact && (
                                        <p className="text-red-500 text-sm mt-1">{formik.errors.emergencyContact}</p>
                                    )}
                                    <TextInput
                                        label="Full Address"
                                        type="text"
                                        name="address"
                                        placeholder="Enter complete address"
                                        value={formik.values.address}
                                        onChange={formik.handleChange}
                                    />
                                    {formik.touched.address && formik.errors.address && (
                                        <p className="text-red-500 text-sm mt-1">{formik.errors.address}</p>
                                    )}
                                </div>
                            </div>


                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={closePopUp}
                                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-8 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors shadow-md"
                            >
                                {editingMemberId ? "Update Member" : "Add Member"}
                            </button>
                        </div>
                    </form>
                </div>
            </AddDialog>
            {cameraOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Take a Selfie</h3>
                        {cameraError ? (
                            <p className="text-sm text-red-500 text-center">{cameraError}</p>
                        ) : (
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                muted
                                className="w-full rounded-md bg-black aspect-video"
                            />
                        )}
                        <canvas ref={canvasRef} className="hidden" />
                        <div className="flex justify-end gap-3 mt-4">
                            {!cameraError && (
                                <button
                                    type="button"
                                    onClick={capturePhoto}
                                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                >
                                    Capture
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={closeCamera}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <AddDialog title="Renew Memberships" isOpen={renew} onClose={closeRenewPopUp}>
                <div className="p-6">
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            if (!selectedPackage) {
                                showErrorToast("Please select a package");
                                return;
                            }
                            if (!startDate) {
                                showErrorToast("Please choose a start date");
                                return;
                            }
                            handleRenewMembership(selectedPackage, startDate);
                        }}
                        className="flex flex-col gap-4"
                    >
                        <SelectInput
                            label="Packages"
                            name="package"
                            value={selectedPackage}
                            onChange={(e: any) => setSelectedPackage(e.target.value)}
                            options={packages.map((type: any) => ({
                                value: type._id,
                                label: type.name,
                            }))}
                        />

                        {/* Package details preview */}
                        {selectedPackageDetails && (
                            <div className="mt-2 rounded-md bg-gray-50 p-3 text-sm text-gray-700 border border-gray-200">
                                <p className="font-semibold mb-1">
                                    {selectedPackageDetails.name}
                                </p>
                                <p>
                                    Duration:{" "}
                                    <span className="font-medium">
                                        {selectedPackageDetails.durationValue}{" "}
                                        {selectedPackageDetails.durationUnit}
                                        {selectedPackageDetails.durationValue > 1
                                            ? "s"
                                            : ""}
                                    </span>
                                </p>
                                <p>
                                    Price:{" "}
                                    <span className="font-medium">
                                        {selectedPackageDetails.price} $
                                    </span>
                                </p>

                            </div>
                        )}

                        <TextInput
                            label="Start Date"
                            type="date"
                            name="startDate"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />

                        <button
                            type="submit"
                            className="bg-blue-500 text-white px-4 py-2 rounded"
                        >
                            Renew Memberships
                        </button>
                    </form>
                </div>
            </AddDialog>

        </div>
    );
};

export default Members;
